import { Button, styled, useTheme } from '@mui/material';
import {
  ACTION_BAR_HEIGHT,
  SIDEBAR_SECTIONS_WITH_PROGRESS_BAR_WIDTH,
} from '../../../constants/layoutSizes';
import { ReactComponent as EditIcon } from '../../../assets/icons/edit.svg';
import { ActionButtonsFooter } from '../../../components/ActionButtonsFooter/ActionButtonsFooter';
import { useCallback, useMemo, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import {
  activeReportState,
  activeSurveyState,
  responsesState,
  userState,
} from '../../../state/UIState';
import {
  ACTIVITY_TYPE,
  KPI_RESPONSE_SUBMITTED_SUBTYPE,
  LoadingId,
  QUESTION_TYPE,
  QuestionOption,
  REPORT_STATUS,
  SURVEY_REQUEST_SUBTYPE,
  SURVEY_STAGE,
  SurveyQuestion,
  User,
} from '../../../types';
import { useLoadingBar } from '../../../hooks/useLoadingBar';
import { REPORTS, useUpdateReport } from '../../../queries/useReports';
import { useNavigate } from 'react-router';
import { flattenTree } from '../../../utils/treeUtilities';
import { useQueryClient } from 'react-query';
import { ROUTES } from '../../../constants/routes';
import { useCreateCompanyActivity } from '../../../queries/useCompanyActivity';

const ActionsWrapper = styled('div')`
  margin-left: ${SIDEBAR_SECTIONS_WITH_PROGRESS_BAR_WIDTH}px;
  position: relative;
  height: ${ACTION_BAR_HEIGHT}px;
`;

const EditButtonIcon = styled(EditIcon)`
  path {
    fill: ${({ theme }) => theme.colors.accent[50]};
  }
`;

export const FooterButtons = () => {
  const [activeReport] = useAtom(activeReportState);
  const [isLoading, setIsLoading] = useState(false);
  const { startLoading, stopLoading } = useLoadingBar();
  const navigate = useNavigate();
  const { colors } = useTheme();
  const queryClient = useQueryClient();

  const [response] = useAtom(responsesState);
  const [activeSurvey] = useAtom(activeSurveyState);
  const user = useAtomValue(userState);
  const { mutate: createCompanyActivity } = useCreateCompanyActivity(activeReport?.company?.id);

  const updateReport = useUpdateReport();

  const onSubmit = useCallback(async () => {
    if (!activeReport) return;
    setIsLoading(true);
    startLoading(LoadingId.completeReport);

    await updateReport.mutateAsync(
      { id: activeReport.id, status: REPORT_STATUS.IN_REVIEW },
      {
        onSuccess: (report) => {
          queryClient.refetchQueries(`${REPORTS}-${activeReport?.company?.id}`);
          queryClient.refetchQueries(`${REPORTS}-${activeReport.id}`);
          navigate(`/${ROUTES.PENDING_REQUESTS}`);

          let type = ACTIVITY_TYPE.SURVEY_REQUEST;
          let subtype: SURVEY_REQUEST_SUBTYPE | KPI_RESPONSE_SUBMITTED_SUBTYPE =
            SURVEY_REQUEST_SUBTYPE.RESPONSE_SUBMITTED;

          if (report.survey.stage === SURVEY_STAGE.CORE_KPI) {
            type = ACTIVITY_TYPE.KPI_RESPONSE_SUBMITTED;
            subtype = KPI_RESPONSE_SUBMITTED_SUBTYPE.KPI_RESPONSE_SUBMITTED;
          }

          createCompanyActivity({
            company: report.company?.id,
            user: (user as User)?.id,
            type,
            subtype,
            value: {
              report,
            },
          });
        },
        onSettled: () => {
          setIsLoading(false);
          stopLoading(LoadingId.completeReport);
        },
      }
    );

    // await sendEvaluationInvites.mutateAsync(activeReport.id);
  }, [
    activeReport,
    startLoading,
    updateReport,
    createCompanyActivity,
    user,
    queryClient,
    navigate,
    stopLoading,
  ]);

  const isSubmitDisabled = useMemo(() => {
    //check if all required questions and subquestions are answered
    if (!activeSurvey || !response) return true;
    const allSections = flattenTree(activeSurvey.sections);
    const allQuestions = allSections.reduce((acc: SurveyQuestion[], section) => {
      const subQuestions: SurveyQuestion[] = [];
      for (const question of section.questions) {
        if (
          question.type === QUESTION_TYPE.SINGLE &&
          question.includeConditions &&
          response[question.id]
        ) {
          const option = response[question.id] as QuestionOption;
          option?.subQuestions && subQuestions.push(...option.subQuestions);
        }
      }
      return [...acc, ...section.questions, ...subQuestions];
    }, []);
    const requiredQuestions = allQuestions.filter((question) => question.isRequired);
    const answeredQuestions = requiredQuestions.filter((question) => response[question.id]);
    return requiredQuestions.length !== answeredQuestions.length;
  }, [response, activeSurvey]);

  const onEditSubmission = async () => {
    startLoading(LoadingId.editSubmission);
    setIsLoading(true);
    await updateReport.mutateAsync(
      { id: activeReport?.id, status: REPORT_STATUS.IN_PROGRESS },
      {
        onError: (e) => {
          console.error(e);
        },
        onSettled: () => {
          stopLoading(LoadingId.editSubmission);
          setIsLoading(false);
        },
      }
    );
  };

  return (
    <ActionsWrapper>
      <ActionButtonsFooter
        onSubmit={onSubmit}
        isSubmitDisabled={isLoading || isSubmitDisabled}
        submitText={'Submit Report'}
        bgColor={colors.primary[0]}
        secondaryButton={
          activeReport?.status === REPORT_STATUS.SUBMITTED ? (
            <Button
              variant='outlined'
              style={{ width: '173px' }}
              onClick={onEditSubmission}
              startIcon={<EditButtonIcon />}
            >
              Edit Submission
            </Button>
          ) : undefined
        }
      />
    </ActionsWrapper>
  );
};
